// Libs
import { Form, Formik } from "formik";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import * as Yup from "yup";
// Types
import { PayerData } from "../../Libs/Types/Payers";
import { FailureResponseWrapper } from "../../Libs/Types/API-Responses";
// UI-Components
import { Button } from "../../UI-Components/Button";
// Endpoints
import { apiAddPayer, apiUpdatePayer } from "../../Endpoints/Payers";
import PayerForm from "./PayerForm";

export default function AddNewPayer({
  handleClosePayer,
  setPayerValues,
  isDisabled = false,
}: {
  handleClosePayer: (manageRefetch?: boolean) => void;
  setPayerValues?: PayerData;
  isDisabled?: boolean;
}) {
  const initialValues: PayerData = {
    id: setPayerValues?.id!,
    uniqueId: setPayerValues?.uniqueId!,
    name: setPayerValues?.name!,
    address: setPayerValues?.address!,
    state: setPayerValues?.state!,
    phoneNumber: setPayerValues?.phoneNumber!,
  };

  // Login Validaiton Schema
  const PayerFormValidationSchema = Yup.object().shape({
    uniqueId: Yup.string()
      .min(5, "Minimun 5 characters required")
      .required("Unique ID is required"),
    name: Yup.string()
      .min(2, "Minimun 2 characters required")
      .required("Name is required"),
    address: Yup.string()
      .min(10, "Minimun 10 characters required")
      .required("Address is required"),
    state: Yup.string()
      .min(3, "Minimun 3 characters required")
      .required("State is required"),
    phoneNumber: Yup.string()
      .min(6, "Minimun 6 characters required")
      .required("Phone is required"),
  });

  // API Response Handling
  const { mutateAsync: createPayer } = useMutation({
    mutationFn: apiAddPayer,
    retry: 0,
    onSuccess: async () => {
      handleClosePayer(true);
    },
    onError: (error: any) => {
      const errorData: FailureResponseWrapper = error.response.data;
      toast.error(errorData?.message ?? "Something Went Wrong", {
        duration: 3000,
      });
    },
    throwOnError: false,
  });

  // API Response Handling
  const { mutateAsync: updatePayer } = useMutation({
    mutationFn: apiUpdatePayer,
    retry: 0,
    onSuccess: async () => {
      handleClosePayer(true);
    },
    onError: (error: any) => {
      const errorData: FailureResponseWrapper = error.response.data;
      toast.error(errorData?.message ?? "Something Went Wrong", {
        duration: 3000,
      });
    },
    throwOnError: false,
  });

  // Handle Payer Submit
  const handleAddPayer = async (values: PayerData) => {
    if ((setPayerValues && setPayerValues !== undefined) || null) {
      await updatePayer({
        payload: {
          payer_name: values.name,
          is_active: true,
          payer_code: values.uniqueId,
          contact_info: {
            address: values.address,
            phoneNumber: values.phoneNumber,
            state: values.state,
          },
        },
        id: initialValues.id!,
      });
    } else {
      await createPayer({
        payer_name: values.name,
        is_active: true,
        payer_code: values.uniqueId,
        contact_info: {
          address: values.address,
          phoneNumber: values.phoneNumber,
          state: values.state,
        },
      });
    }
  };
  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={handleAddPayer}
        validationSchema={PayerFormValidationSchema}
      >
        {({ isValid }) => (
          <Form className="w-full">
            <PayerForm isDisabled={isDisabled} />
            {!isDisabled && (
              <div className="mt-5 flex justify-between gap-2">
                <Button
                  type="submit"
                  variant={"default"}
                  className="w-full"
                  disabled={!isValid}
                >
                  {(setPayerValues && setPayerValues !== undefined) || null
                    ? "Update"
                    : "Add"}
                </Button>
                <Button
                  type="button"
                  variant={"cancel"}
                  className="w-full"
                  onClick={() => handleClosePayer()}
                >
                  Cancel
                </Button>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
}
